import type { FC } from 'react';
import { useState } from 'react';

import { Box, List } from '@packages/shared';

import type { SxProps } from '@mui/system';
import { useSelect } from '../../hooks/useSelect';
import { ensureUniqueness } from '../../utils/ensureUniqueness';
import type { GroupedFilterValues, BasicFilterValue } from '../../api/rest/types';
import { BasicFilterSearchBox } from '../BasicFilterSearchBox';
import { BasicFilterValueGroup } from '../BasicFilterValueGroup';

export type BasicFilterValueListProps = {
  /** The name of the currently displayed filter */
  filterName: string;
  /** Multiple groups of grouped filter values to display
   * @default []
   * */
  groupedValues: GroupedFilterValues<BasicFilterValue>[];
  /** If `true`, the component will show a search box to allow a user to manually search for items */
  isSearchable?: boolean;
  /** If `true`, the component uses single selection instead of multiple selection */
  isSingleSelection?: boolean;
  /** Currently selected filter value ids */
  selectedIds: string[];
  /** Callback fired when the filter selection changes */
  onChange?: (selectedIds: string[]) => void;
  /** Additional styles which should be applied on the container */
  contentContainerStyle?: SxProps;
};

function useSearchableFilterValues(groupedValues: GroupedFilterValues<BasicFilterValue>[]): {
  filteredGroups: GroupedFilterValues<BasicFilterValue>[];
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
} {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredGroups = searchQuery
    ? groupedValues?.map(({ groupName, values }) => ({
        groupName,
        values: values.filter((value) =>
          value.name.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      }))
    : groupedValues;

  return { filteredGroups, searchQuery, setSearchQuery };
}

/**
 * Displays a list of multi-selectable filter values, optionally grouped and/or searchable
 * */
export const BasicFilterValueList: FC<BasicFilterValueListProps> = ({
  filterName,
  groupedValues: groups = [],
  isSearchable,
  isSingleSelection,
  selectedIds,
  contentContainerStyle,
  onChange,
}) => {
  const uniqueGroups = ensureUniqueness(groups);

  const { filteredGroups, searchQuery, setSearchQuery } = useSearchableFilterValues(uniqueGroups);

  const { toggleId } = useSelect([selectedIds, onChange], { isSingleSelection });

  return (
    <Box sx={{ display: 'contents' }}>
      {isSearchable && (
        <BasicFilterSearchBox
          filterName={filterName}
          searchQuery={searchQuery}
          onChange={setSearchQuery}
        />
      )}
      <List
        sx={{
          flex: '1',
          overflow: 'auto',
          padding: 0,
          marginTop: 0.25,
          ...contentContainerStyle,
        }}
      >
        {filteredGroups?.map((group) => (
          <BasicFilterValueGroup
            key={`group-${group.groupName}`}
            group={group}
            isSingleSelection={isSingleSelection}
            selectedIds={selectedIds}
            onItemClick={toggleId}
          />
        ))}
      </List>
    </Box>
  );
};
